import type { AuditRecord } from '../data/audits';
import type { QuestionStat, SectionStat } from './analytics';
import type { DistrictSummary, TemplateTotals } from './districtSummary';

/**
 * Reading monthly district summaries back (client side; the builder and
 * parser are in districtSummary.ts, shared with the server and seeder).
 */

/** Slim AuditRecords (no section scores, failure summary or KPIs) — what the trend, tiles, auditors and grades read. */
export function summaryAudits(summaries: readonly DistrictSummary[]): AuditRecord[] {
  const seen = new Set<number>();
  const out: AuditRecord[] = [];
  for (const s of summaries) {
    for (const e of s.a) {
      // A store that moved district mid-month can be listed twice until a rebuild.
      if (seen.has(e.i)) continue;
      seen.add(e.i);
      out.push({
        id: e.i,
        storeId: e.s,
        templateId: e.t,
        templateVersion: e.v,
        auditorUpn: e.u,
        status: 'submitted',
        startedAt: '',
        submittedAt: e.at,
        score: e.sc,
        criticalFailed: e.c === 1,
        sectionScores: null,
        failureSummary: null,
        kpis: null,
        party: e.p ?? 'dm',
      });
    }
  }
  return out.sort((x, y) => y.submittedAt.localeCompare(x.submittedAt));
}

/** The template totals of the chosen template rows (all when `templateIds` is null), from months ≥ `fromMonth` ('' = all). */
function pickTotals(summaries: readonly DistrictSummary[], templateIds: ReadonlySet<number> | null, fromMonth: string): TemplateTotals[] {
  return summaries
    .filter((s) => !fromMonth || s.month >= fromMonth)
    .flatMap((s) => Object.entries(s.t).filter(([id]) => !templateIds || templateIds.has(Number(id))).map(([, tot]) => tot));
}

/** sectionAverages (analytics.ts), from the stored totals — same output shape and order. */
export function sectionAveragesFromSummaries(
  summaries: readonly DistrictSummary[],
  titleOf: (sectionId: string) => string | undefined,
  templateIds: ReadonlySet<number> | null,
  fromMonth: string,
): SectionStat[] {
  const by = new Map<string, { title: string; sum: number; n: number }>();
  for (const tot of pickTotals(summaries, templateIds, fromMonth)) {
    for (const [id, [sum, n]] of Object.entries(tot.sec)) {
      const title = titleOf(id);
      if (title === undefined) continue;
      const e = by.get(id) ?? { title, sum: 0, n: 0 };
      e.sum += sum;
      e.n += n;
      by.set(id, e);
    }
  }
  return [...by.entries()]
    .filter(([, e]) => e.n > 0)
    .map(([sectionId, e]) => ({ sectionId, title: e.title, avg: e.sum / e.n, audits: e.n }))
    .sort((x, y) => x.avg - y.avg || x.title.localeCompare(y.title));
}

/** questionFailureRates (analytics.ts), from the stored totals — same output shape and order. */
export function questionFailureRatesFromSummaries(
  summaries: readonly DistrictSummary[],
  templateIds: ReadonlySet<number> | null,
  fromMonth: string,
  minAudits = 1,
): QuestionStat[] {
  const by = new Map<string, { checked: number; failed: number; audits: number }>();
  for (const tot of pickTotals(summaries, templateIds, fromMonth)) {
    for (const [id, [checked, failed, audits]] of Object.entries(tot.q)) {
      const s = by.get(id) ?? { checked: 0, failed: 0, audits: 0 };
      s.checked += checked;
      s.failed += failed;
      s.audits += audits;
      by.set(id, s);
    }
  }
  return [...by.entries()]
    .filter(([, s]) => s.audits >= minAudits && s.checked > 0)
    .map(([questionId, s]) => ({ questionId, ...s, failRate: s.failed / s.checked }))
    .sort((x, y) => y.failRate - x.failRate || y.failed - x.failed || x.questionId.localeCompare(y.questionId));
}

/** Audits of the chosen templates without a failure summary (the "N audits not summarized" note). */
export function unsummarizedFromSummaries(
  summaries: readonly DistrictSummary[],
  templateIds: ReadonlySet<number> | null,
  fromMonth: string,
): number {
  // A month whose totals were dropped (tx — too big for the column): every staff
  // audit of the chosen templates in it counts as not summarized.
  const dropped = summaries
    .filter((s) => s.tx === 1 && (!fromMonth || s.month >= fromMonth))
    .reduce((n, s) => n + s.a.filter((e) => e.p !== 'store' && (!templateIds || templateIds.has(e.t))).length, 0);
  return pickTotals(summaries, templateIds, fromMonth).reduce((n, tot) => n + tot.un, 0) + dropped;
}
